import React from 'react';
import Feather from '@expo/vector-icons/Feather';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaRadius, PaprikaShadows, PaprikaSpacing } from '../../styles/theme';
import { SuggestionBadge } from '../../services/weekGeneratorService';
import { PersonalRecipeIndexEntry } from '../../services/personalRecipeIndex';
import { RemoteImage } from '../shared/RemoteImage';
import { RecipePlaceholder } from '../shared/RecipePlaceholder';

interface SuggestionCardProps {
  recipe: PersonalRecipeIndexEntry;
  badges: SuggestionBadge[];
  onPress: () => void;
  onBadgePress?: (badgeType: SuggestionBadge['type']) => void;
}

const CARD_WIDTH = 140;
const CARD_HEIGHT = 180;
const IMAGE_HEIGHT = 100;

export const SuggestionCard: React.FC<SuggestionCardProps> = ({
  recipe,
  badges,
  onPress,
  onBadgePress,
}) => {
  const cookTime = recipe.readyInMinutes || 0;

  // Get the most relevant badge to display
  const { t } = useTranslation();
  const primaryBadge = badges[0];

  return (
    <TouchableOpacity
      style={styles.card}
      onPress={onPress}
      activeOpacity={0.85}
      testID="suggestion-card"
    >
      {/* Image Container */}
      <View style={styles.imageContainer}>
        <RemoteImage
          uri={recipe.image}
          variant="thumb"
          style={styles.recipeImage}
          resizeMode="cover"
          fallback={
            <RecipePlaceholder
              recipe={recipe}
              size={IMAGE_HEIGHT}
              style={styles.placeholder}
              showDietDot
              testID="suggestion-card-placeholder"
            />
          }
        />

        {/* Cook time badge on image */}
        {cookTime > 0 && (
          <View style={styles.cookTimeBadge}>
            <Text style={styles.cookTimeText}>{cookTime} min</Text>
          </View>
        )}
      </View>

      {/* Card Content */}
      <View style={styles.content}>
        <Text style={styles.recipeTitle} numberOfLines={2} testID="suggestion-card-title">
          {recipe.title}
        </Text>

        {/* Badge - tappable to filter */}
        {primaryBadge && (
          <TouchableOpacity
            style={[styles.badge, getBadgeStyle(primaryBadge.type)]}
            onPress={(e) => {
              e.stopPropagation();
              onBadgePress?.(primaryBadge.type);
            }}
            activeOpacity={0.7}
            hitSlop={{ top: 5, bottom: 5, left: 5, right: 5 }}
          >
            <Feather
              name={BADGE_ICON[primaryBadge.type]}
              size={11}
              color={Paprika.textSecondary}
              style={styles.badgeIcon}
            />
            <Text style={styles.badgeText}>{badgeText(primaryBadge, t)}</Text>
          </TouchableOpacity>
        )}
      </View>
    </TouchableOpacity>
  );
};

/**
 * The generator writes an English `label`. Only two kinds print data rather
 * than copy — a cook time ("20 min") and a cuisine name — so every other kind
 * is drawn from its type.
 */
function badgeText(badge: SuggestionBadge, t: (key: string) => string): string {
  if (badge.type === 'quick' || badge.type === 'cuisine') return badge.label;
  return t(`thisWeek.suggestionBadges.${badge.type}`);
}

/**
 * The badge label used to carry its own emoji. The icon belongs here instead,
 * keyed off the type the badge already reports, so the label stays plain text
 * and translatable.
 */
const BADGE_ICON: Record<SuggestionBadge['type'], React.ComponentProps<typeof Feather>['name']> = {
  quick: 'zap',
  veggie: 'feather',
  fish: 'anchor',
  favorite: 'star',
  new: 'sunrise',
  cuisine: 'globe',
};

// Get badge style based on type
const getBadgeStyle = (type: SuggestionBadge['type']) => {
  switch (type) {
    case 'quick':
      return { backgroundColor: '#E3F2FD' }; // Light blue
    case 'veggie':
      return { backgroundColor: '#E8F5E9' }; // Light green
    case 'fish':
      return { backgroundColor: '#E0F7FA' }; // Light cyan
    case 'favorite':
      return { backgroundColor: '#FFF8E1' }; // Light amber
    case 'new':
      return { backgroundColor: '#F3E5F5' }; // Light purple
    case 'cuisine':
      return { backgroundColor: '#FBE9E7' }; // Light deep orange
    default:
      return { backgroundColor: Paprika.surfaceLight };
  }
};

const styles = StyleSheet.create({
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.md,
    ...PaprikaShadows.card,
    marginRight: PaprikaSpacing.sm,
  },
  imageContainer: {
    height: IMAGE_HEIGHT,
    borderTopLeftRadius: PaprikaRadius.md,
    borderTopRightRadius: PaprikaRadius.md,
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceLight,
  },
  recipeImage: {
    width: '100%',
    height: '100%',
  },
  placeholder: {
    width: '100%',
    height: '100%',
  },
  cookTimeBadge: {
    position: 'absolute',
    bottom: PaprikaSpacing.xs,
    right: PaprikaSpacing.xs,
    backgroundColor: 'rgba(27, 28, 26, 0.62)',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: PaprikaRadius.sm,
  },
  cookTimeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
    padding: PaprikaSpacing.sm,
    justifyContent: 'space-between',
  },
  recipeTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    lineHeight: 16,
    color: Paprika.textPrimary,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: PaprikaRadius.sm,
  },
  badgeIcon: {
    marginRight: 4,
  },
  badgeText: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    color: Paprika.textSecondary,
  },
});

export default SuggestionCard;
